// Operators.tsx — platform operator list and operator detail.
//
// An operator is an admin-role user in the platform tenant. Adding one grants
// cross-tenant reach over every customer, so both mutations are reason-required
// and the server enforces two guards this UI must not paper over: the last
// operator cannot be removed, and an operator cannot remove themselves.
//
// "Reset password" here means exactly what it means on the customer Users tab:
// re-issue a set-password invite. No endpoint sets or reveals a password.
//
// Operator DETAIL is assembled from endpoints that exist — the list row plus the
// operator's own rows in the audit log. The prototype's "active sessions" tile is
// deliberately absent: there is no session-store endpoint, and a tile showing a
// plausible-looking zero is worse than no tile.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { PT } from "../../platformTheme";
import { pget, pmutate, perr } from "./api";
import {
  PCard, PSection, PBtn, PBadge, PBanner, PEmpty, PSpinner, PTable, PRow,
  PModal, PReasonPrompt, PInput, PSelect, PStat, PGrid, PId,
  th, td, fmtDate,
} from "./ui";

interface Operator {
  id: string; email: string; name: string | null; role: string;
  is_active: boolean; totp_enabled: boolean; must_change_password: boolean; created_at: string;
}

export const Operators: React.FC = () => {
  const [ops, setOps] = useState<Operator[] | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [adding, setAdding] = useState(false);
  const [nu, setNu] = useState({ email: "", name: "" });
  const [prompt, setPrompt] = useState<null | { title: string; detail: React.ReactNode; danger?: boolean; run: (r: string) => Promise<void> }>(null);
  const [busy, setBusy] = useState(false);
  const [pErr, setPErr] = useState("");

  const load = useCallback(async () => {
    setErr("");
    const r = await pget("/operators");
    if (!r.ok) { setErr(perr(r, "Could not load operators")); setOps([]); return; }
    setOps(r.data?.operators || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (reason: string) => {
    if (!prompt) return;
    setBusy(true); setPErr("");
    try { await prompt.run(reason); setPrompt(null); await load(); }
    catch (e: any) { setPErr(e?.message || "Action failed"); }
    finally { setBusy(false); }
  };

  const activeCount = (ops || []).filter((o) => o.is_active).length;
  const current = (ops || []).find((o) => o.id === openId) || null;

  if (current) {
    return (
      <OperatorDetail
        op={current}
        soleOperator={activeCount <= 1}
        onBack={() => setOpenId(null)}
        onAction={(p) => { setPErr(""); setPrompt(p); }}
      />
    );
  }

  return (
    <>
      <PSection
        title="Operators"
        sub={ops ? `${ops.length} total · ${activeCount} active` : undefined}
        right={<><PBtn small onClick={load}>Refresh</PBtn>{" "}<PBtn small v="primary" onClick={() => setAdding(true)}>+ Add operator</PBtn></>}
      >
        <PBanner>{err}</PBanner>
        <PBanner tone="ok">{msg}</PBanner>
        <PBanner tone="warn">
          An operator has cross-tenant reach over every customer. Both adding and removing one is
          recorded in the operator audit log with the reason given.
        </PBanner>

        <PCard style={{ padding: 0 }}>
          {ops === null ? <PSpinner /> : ops.length === 0 ? <PEmpty>No operators.</PEmpty> : (
            <PTable
              minWidth={700}
              head={<>
                <th style={th}>Operator</th><th style={th}>Role</th><th style={th}>2FA</th>
                <th style={th}>Status</th><th style={th}>Created</th>
              </>}
            >
              {ops.map((o) => (
                <PRow key={o.id} onClick={() => setOpenId(o.id)}>
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>{o.name || "—"}</div>
                    <div style={{ fontSize: 11.5, color: PT.txD }}>{o.email}</div>
                  </td>
                  <td style={td}><PBadge tone="info">{o.role}</PBadge></td>
                  <td style={td}>{o.totp_enabled ? <PBadge tone="ok">On</PBadge> : <PBadge tone="warn">Off</PBadge>}</td>
                  <td style={td}>{o.is_active ? <PBadge tone="ok">Active</PBadge> : <PBadge tone="neutral">Inactive</PBadge>}</td>
                  <td style={{ ...td, color: PT.txM, fontSize: 11.5 }}>{fmtDate(o.created_at)}</td>
                </PRow>
              ))}
            </PTable>
          )}
        </PCard>
      </PSection>

      <PModal
        open={adding}
        onClose={() => setAdding(false)}
        title="Add platform operator"
        footer={<>
          <PBtn onClick={() => setAdding(false)}>Cancel</PBtn>
          <PBtn
            v="primary"
            disabled={!nu.email.trim()}
            onClick={() => {
              setAdding(false); setPErr("");
              setPrompt({
                title: "Add operator",
                detail: (
                  <>
                    <strong>{nu.email}</strong> will gain <strong>cross-tenant operator access to every
                    customer</strong>. An existing platform-tenant user is promoted; otherwise an account
                    is created. Either way a set-password invite is sent — no password is shown.
                  </>
                ),
                run: async (reason) => {
                  const r = await pmutate("POST", "/operators", reason, {
                    email: nu.email.trim().toLowerCase(), name: nu.name.trim(),
                  });
                  if (!r.ok) throw new Error(perr(r));
                  setMsg(`Operator invite issued to ${nu.email}.`);
                  setNu({ email: "", name: "" });
                },
              });
            }}
          >Continue</PBtn>
        </>}
      >
        <PInput label="Email" value={nu.email} onChange={(v) => setNu({ ...nu, email: v })} autoFocus />
        <PInput label="Name" value={nu.name} onChange={(v) => setNu({ ...nu, name: v })} />
      </PModal>

      <PReasonPrompt
        open={!!prompt}
        title={prompt?.title || ""}
        detail={prompt?.detail}
        danger={prompt?.danger}
        busy={busy}
        error={pErr}
        onCancel={() => setPrompt(null)}
        onConfirm={run}
      />
    </>
  );
};

// ── Detail ───────────────────────────────────────────────────────────────────

const OperatorDetail: React.FC<{
  op: Operator;
  soleOperator: boolean;
  onBack: () => void;
  onAction: (p: { title: string; detail: React.ReactNode; danger?: boolean; run: (r: string) => Promise<void> }) => void;
}> = ({ op, soleOperator, onBack, onAction }) => {
  const [audit, setAudit] = useState<any[] | null>(null);

  useEffect(() => {
    (async () => {
      // No per-operator audit endpoint exists; read the cross-tenant log and
      // filter to this actor. Bounded server-side at 500.
      const r = await pget("/audit", { limit: 500 });
      setAudit(r.ok ? (r.data?.entries || []).filter((e: any) => e.actor_user_id === op.id) : []);
    })();
  }, [op.id]);

  const actions30d = useMemo(() => {
    if (!audit) return null;
    const cut = Date.now() - 30 * 864e5;
    return audit.filter((e) => new Date(e.occurred_at).getTime() >= cut).length;
  }, [audit]);

  return (
    <PSection
      title={op.name || op.email}
      sub={op.email}
      right={<PBtn small onClick={onBack}>← Operators</PBtn>}
    >
      <PGrid min={170}>
        <PStat label="Role" value={op.role} />
        <PStat label="2FA" value={op.totp_enabled ? "Enabled" : "Off"} />
        <PStat label="Status" value={op.is_active ? "Active" : "Inactive"} />
        <PStat
          label="Actions · 30d"
          value={actions30d === null ? "…" : actions30d}
          sub="from the operator audit log"
        />
      </PGrid>

      <div style={{ marginTop: 20 }} />
      <PSection title="Account actions">
        <PCard>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <PBtn
              onClick={() => onAction({
                title: "Re-issue set-password invite",
                detail: (
                  <>
                    Sends a fresh set-password link to <strong>{op.email}</strong>.
                    <div style={{ marginTop: 7, color: PT.txM }}>No password is generated or displayed.</div>
                  </>
                ),
                run: async (reason) => {
                  // Operators live in the platform tenant, so the tenant-scoped
                  // reset-invite route is the same door, addressed at that tenant.
                  const r = await pmutate("POST", `/operators`, reason, { email: op.email, reissue_invite: true });
                  if (!r.ok) throw new Error(perr(r));
                },
              })}
            >Reset password</PBtn>

            <PBtn
              v="danger"
              disabled={soleOperator}
              title={soleOperator ? "The server refuses removal of the last operator" : undefined}
              onClick={() => onAction({
                title: "Remove operator",
                danger: true,
                detail: (
                  <>
                    Revokes cross-tenant operator access for <strong>{op.email}</strong>.
                    <div style={{ marginTop: 7, color: PT.txM }}>
                      The server independently refuses this for the last operator and for self-removal.
                    </div>
                  </>
                ),
                run: async (reason) => {
                  const r = await pmutate("DELETE", `/operators/${op.id}`, reason);
                  if (!r.ok) throw new Error(perr(r));
                  onBack();
                },
              })}
            >Remove operator</PBtn>
          </div>
          {soleOperator && (
            <div style={{ fontSize: 11.5, color: PT.warn, marginTop: 10 }}>
              This is the only active operator. Removal is blocked — locking every operator out of
              the control plane is unrecoverable from inside the product.
            </div>
          )}
        </PCard>
      </PSection>

      <PSection title="Recent actions" sub="This operator's entries in the cross-tenant audit log">
        <PCard style={{ padding: 0 }}>
          {audit === null ? <PSpinner /> : audit.length === 0 ? (
            <PEmpty>No recorded actions.</PEmpty>
          ) : (
            <PTable
              minWidth={680}
              head={<><th style={th}>Time</th><th style={th}>Action</th><th style={th}>Customer</th><th style={th}>Reason</th></>}
            >
              {audit.slice(0, 100).map((e) => (
                <PRow key={e.id}>
                  <td style={{ ...td, whiteSpace: "nowrap", color: PT.txM, fontSize: 11.5 }}>{fmtDate(e.occurred_at)}</td>
                  <td style={td}><PBadge tone="info">{e.action}</PBadge></td>
                  <td style={td}><PId id={e.target_tenant_id} /></td>
                  <td style={{ ...td, color: PT.txM }}>{e.reason || "—"}</td>
                </PRow>
              ))}
            </PTable>
          )}
        </PCard>
      </PSection>
    </PSection>
  );
};
